import React, { useState, useRef, useMemo, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { useTranslation } from "react-i18next";
import { Row, Col, Spinner } from "react-bootstrap";
import DatePicker from "react-widgets/DatePicker";
import moment from "moment";

//Redux
import { useSelector } from "react-redux"; //useDispatch

import Constants from "../../common/Constants";
import { DropdownListField } from "../../common/formElements/DropdownListField";
import { validateReportDateRange } from "./util";

const newId = () => Math.random().toString(36).slice(2);

const ProdStatusFilterBox = (props) => {
  const { t } = useTranslation();
  const formRef = useRef(null);

  const getAllSizeCode = useSelector((state) => state?.common?.sizeMaster);
  const getAllDepartmentMaster = useSelector(
    (state) => state?.common?.departmentMaster,
  );
  const getAllMachineMaster = useSelector(
    (state) => state?.common?.machineMaster,
  );
  const getAllCustomerMaster = useSelector(
    (state) => state?.common?.customerMaster,
  );
  const getAllPlanTypeMaster = useSelector(
    (state) => state?.common?.planTypeMaster,
  );
  const getAllConsumptionTypes = useSelector(
    (state) => state?.common?.data?.consumptionTypes,
  );

  let filterDayLimit = useSelector((state) => state?.common?.data?.filterDaysLimit); // User Details from Store

  const [allSizeCode, setAllSizeCode] = useState([]);
  const [allDepartmentMaster, setAllDepartmentMaster] = useState([]);
  const [allMachineMaster, setAllMachineMaster] = useState([]);
  const [allCustomerMaster, setAllCustomerMaster] = useState([]);
  const [allPlanTypeMaster, setAllPlanTypeMaster] = useState([]);
  const [allConsumptionTypes, setConsumptionTypes] = useState([]);

  // console.log(">> props.pageName", props.pageName);
  // Options allowed
  const allowedOptions = useMemo(() => {
    if (props.pageName === "CHEMICAL_BATCH_REPORT") {
      return [
        { value: "chemicalBatchNo", label: "Consumables Batches" },
        {
          value: "jobcard",
          label: t("msg_reports.msg_production_status.job_card"),
        },
        {
          value: "sizeCode",
          label: t("msg_reports.msg_production_status.size"),
        },
      ];
    } else if (props.pageName === "BULK_CONSUMPTION_REPORTS") {
      return [
        { value: "chemicalBatchNo", label: "Consumables Batches" },
        {
          value: "deptCode",
          label: "Department",
        },
        { value: "Custom", label: t("msg_common.custom") },
      ];
    } else if (props.pageName === "QUALITY_CERTIFICATE") {
      return [
        {
          value: "jobcard",
          label: t("msg_reports.msg_production_status.job_card"),
        },
        {
          value: "sizeCode",
          label: t("msg_reports.msg_production_status.size"),
        },
        { value: "Custom", label: t("msg_common.custom") },
        { value: "QC_Status", label: "QC Status" },
      ];
    } else if (props.pageName === "CONSUMPTION_QTY_REPORT") {
      return [
        {
          value: "jobcard",
          label: t("msg_reports.msg_production_status.job_card"),
        },
        {
          value: "sizeCode",
          label: t("msg_reports.msg_production_status.size"),
        },
        {
          value: "deptCode",
          label: "Department",
        },
        {
          value: "machineCode",
          label: "Machine",
        },
        { value: "consumptionType", label: "Consumption Type" },
        { value: "Custom", label: t("msg_common.custom") },
      ];
    } else if (props.pageName === Constants.MASTER.CONSUMABLE.name) {
      return [
        {
          value: "sizeCode",
          label: t("msg_reports.msg_production_status.size"),
        },
        {
          value: "deptCode",
          label: "Department",
        },
        { value: "consumptionType", label: "Consumption Type" },
        { value: "consumptionCode", label: "Consumption Code" },
      ];
    } else if (props.pageName === Constants.REPORTS.REWORKREPORTS.pageName) {
      return [
        {
          value: "jobcard",
          label: t("msg_reports.msg_production_status.job_card"),
        },
        {
          value: "sizeCode",
          label: t("msg_reports.msg_production_status.size"),
        },
        {
          value: "deptCode",
          label: t("msg_reports.msg_production_status.currentDepartment"),
        },
        { value: "Custom", label: t("msg_common.complete_date") },
      ];
    }

    const arr = [
      {
        value: "jobcard",
        label: t("msg_reports.msg_production_status.job_card"),
      },
      { value: "sizeCode", label: t("msg_reports.msg_production_status.size") },
      { value: "quantityPerJobCard", label: "Quantity Per JobCard" },
      { value: "delayedBy", label: "Delayed By" },
    ];

    if (props.deptOptionReq) {
      arr.push({
        value: "deptCode",
        label: t("msg_reports.msg_production_status.currentDepartment"),
      });
    }
    if (props.jobcardStatusReq) {
      arr.push({
        value: "jobcardStatus",
        label: t("msg_reports.msg_production_status.jobcardStatus"),
      });
    }
    if (props.customer) {
      arr.push({
        value: "customer",
        label: t("msg_reports.msg_production_status.customer"),
      });
    }
    if (props.required_date) {
      arr.push({
        value: "required_date",
        label: t("msg_reports.msg_production_status.required_date"),
      });
    }
    if (props.plan) {
      arr.push({
        value: "plan",
        label: t("msg_reports.msg_production_status.plan"),
      });
    }
    if (props.planType) {
      arr.push({
        value: "planType",
        label: t("msg_reports.msg_production_status.planType"),
      });
    }
    if (props.CompletedDate) {
      arr.push({ value: "Custom", label: t("msg_common.complete_date") });
    }

    if (props.pageName === Constants.REPORTS.PRODUCTIONSTATUS.pageName) {
      arr.splice(
        arr.findIndex((item) => item.value === "delayedBy"),
        1,
      );
    }

    return arr;
  }, [
    props.pageName,
    props.deptOptionReq,
    props.jobcardStatusReq,
    props.customer,
    props.required_date,
    props.plan,
    props.planType,
    props.CompletedDate,
    t,
  ]);

  const defaultOption = allowedOptions[0]?.value || "jobcard";

  // Each row = { id, option, value, value2 }
  // For "Custom" / "required_date": value = From, value2 = To
  const [filters, setFilters] = useState([
    { id: newId(), option: defaultOption, value: "", value2: "" },
  ]);

  const jobcardStatusdata = [
    { id: "Inprogress", name: "In Progress" },
    { id: "Open", name: "NOT Acknowledged" },
    { id: "Stopped", name: "Stopped" },
    { id: "Acknowledged", name: "Acknowledged" },
    { id: "Complete", name: "Complete" },
  ];

  const qc_status_data = [
    { id: "edit", name: "Edit Report" },
    { id: "generate", name: "Generate Report" },
  ];

  // Helpers
  const resetToDefault = () => {
    setFilters([{ id: newId(), option: defaultOption, value: "", value2: "" }]);
  };

  const addFilter = () => {
    setFilters((prev) => [
      ...prev,
      { id: newId(), option: defaultOption, value: "", value2: "" },
    ]);
  };

  const removeFilter = (id) => {
    setFilters((prev) =>
      prev.length === 1 ? prev : prev.filter((f) => f.id !== id),
    );
  };

  const updateOption = (id, option) => {
    setFilters((prev) =>
      prev.map((f) =>
        f.id === id ? { ...f, option, value: "", value2: "" } : f,
      ),
    );
  };

  const updateValue = (id, newValue) => {
    setFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, value: newValue } : f)),
    );
  };

  // 🔑 Simple & reliable: directly write to "value" / "value2"
  const updateDates = (id, field, dateObj) => {
    const formatted =
      dateObj && moment(dateObj).isValid()
        ? moment(dateObj).format("YYYY-MM-DD")
        : "";

    setFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [field]: formatted } : f)),
    );

    const row = filters.find((filter) => filter.id === id);
    if (row) {
      validateReportDateRange(
        field === "value" ? formatted : row.value,
        field === "value2" ? formatted : row.value2,
        filterDayLimit,
      );
    }
  };

  // Build filter object:
  // jobcard, sizeCode, deptCode, getStatus, chemicalBatchNo, qcStatus,
  // customer, plan, planType,
  // requiredFrom, requiredTo,
  // required_date_from, required_date_to
  const buildFilterObj = () => {
    const acc = {};

    const addKey = (key, val) => {
      if (val === "" || val === undefined || val === null) return;
      if (!(key in acc)) {
        acc[key] = val;
      } else {
        if (Array.isArray(acc[key])) {
          if (!acc[key].includes(val)) acc[key].push(val);
        } else if (acc[key] !== val) {
          acc[key] = [acc[key], val];
        }
      }
    };

    const froms = [];
    const tos = [];
    const reqDateFroms = [];
    const reqDateTos = [];

    filters.forEach((f) => {
      switch (f.option) {
        case "jobcard":
          addKey("jobcard", f.value);
          break;
        case "sizeCode":
          addKey("sizeCode", f.value?.trim()?.toUpperCase());
          break;
        case "deptCode":
          addKey("deptCode", f.value);
          break;
        case "jobcardStatus":
          addKey("getStatus", f.value);
          break;
        case "chemicalBatchNo":
          addKey("chemicalBatchNo", f.value);
          break;
        case "QC_Status":
          addKey("qcStatus", f.value);
          break;

        case "customer":
          addKey("customer", f.value);
          break;
        case "plan":
          addKey("plan", f.value);
          break;
        case "planType":
          addKey("planType", f.value);
          break;
        case "machineCode":
          addKey("machineCode", f.value);
          break;
        case "consumptionType":
          addKey("consumptionType", f.value);
          break;
        case "consumptionCode":
          addKey("consumptionCode", f.value);
          break;
        // Custom date range -> requiredFrom / requiredTo
        case "Custom":
          if (f.value) froms.push(f.value);
          if (f.value2) tos.push(f.value2);
          break;

        // Required Date -> required_date_from / required_date_to
        case "required_date":
          if (f.value) reqDateFroms.push(f.value);
          if (f.value2) reqDateTos.push(f.value2);
          break;
        case "quantityPerJobCard":
          addKey("quantityPerJobCard", f.value);
          break;
        case "delayedBy":
          addKey("delayedBy", f.value);
          break;

        default:
          break;
      }
    });

    // Custom date merging
    if (froms.length) acc.requiredFrom = froms.sort()[0];
    if (tos.length) acc.requiredTo = tos.sort()[tos.length - 1];

    // required_date date merging
    if (reqDateFroms.length) acc.required_date_from = reqDateFroms.sort()[0];
    if (reqDateTos.length)
      acc.required_date_to = reqDateTos.sort()[reqDateTos.length - 1];

    return acc;
  };

  // Actions
  const validateFilterDateRanges = (filterObj) =>
    validateReportDateRange(
      filterObj.requiredFrom,
      filterObj.requiredTo,
      filterDayLimit,
    ) &&
    validateReportDateRange(
      filterObj.required_date_from,
      filterObj.required_date_to,
      filterDayLimit,
    );

  const handleSearch = () => {
    const filterObj = buildFilterObj();
    if (!validateFilterDateRanges(filterObj)) {
      return;
    }

    // console.log("SEARCH FILTER OBJ", filterObj);
    props.searchData(filterObj);
  };

  const handleDownload = (specialPSReport) => {
    const filterObj = buildFilterObj();
    if (!validateFilterDateRanges(filterObj)) {
      return;
    }

    props.handleDownload({
      ...filterObj,
      download: true,
      specialReport: specialPSReport,
    });
  };

  const handleCancel = () => {
    resetToDefault();
    props.resetFilerBox(false);
    props.resetSearch();
  };

  // Render per row inputs
  const renderRowInput = (row) => {
    const commonId = "exampleForm.ControlInput1";

    if (row.option === "chemicalBatchNo") {
      return (
        <Col className="px-3" xs={10} sm={12} md={7} lg={7}>
          <Form.Group className="mb-3" controlId={commonId}>
            <Form.Label className="col-form-label">
              Enter the Consumables Batch No:
            </Form.Label>
            <Form.Control
              type="text"
              value={row.value || ""}
              placeholder="e.g: 3001"
              onChange={(e) => updateValue(row.id, e.target.value)}
              maxLength={Constants.MASTER.VALIDATION.maxLengthName}
              required
            />
            <Form.Control.Feedback type="invalid">
              {t("msg_master_list.msg_department.department_name_validation")}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      );
    }

    if (row.option === "jobcard") {
      return (
        <Col className="px-3" xs={10} sm={12} md={7} lg={7}>
          <Form.Group className="mb-3" controlId={commonId}>
            <Form.Label className="col-form-label">
              {t("msg_reports.msg_production_status.job_card")} :
            </Form.Label>
            <Form.Control
              type="text"
              value={row.value || ""}
              placeholder="e.g: JOB-001"
              onChange={(e) => updateValue(row.id, e.target.value)}
              maxLength={Constants.MASTER.VALIDATION.maxLengthName}
              required
            />
            <Form.Control.Feedback type="invalid">
              {t("msg_master_list.msg_department.department_name_validation")}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      );
    }

    if (row.option === "quantityPerJobCard") {
      return (
        <Col className="px-3" xs={10} sm={12} md={7} lg={7}>
          <Form.Group className="mb-3" controlId={commonId}>
            <Form.Label className="col-form-label">
              Quantity Per JobCard :
            </Form.Label>
            <Form.Control
              type="text"
              value={row.value || ""}
              placeholder="e.g: 0, 10, 1333, ..."
              onChange={(e) => updateValue(row.id, e.target.value)}
              maxLength={Constants.MASTER.VALIDATION.maxLengthName}
              required
            />
          </Form.Group>
        </Col>
      );
    }

    if (row.option === "delayedBy") {
      return (
        <Col className="px-3" xs={10} sm={12} md={7} lg={7}>
          <Form.Group className="mb-3" controlId={commonId}>
            <Form.Label className="col-form-label">
              Enter the Delayed By in Number of Days :
            </Form.Label>
            <Form.Control
              type="text"
              value={row.value || ""}
              placeholder="e.g: 5, 10, 25, 45..."
              onChange={(e) => updateValue(row.id, e.target.value)}
              maxLength={Constants.MASTER.VALIDATION.maxLengthName}
              required
            />
          </Form.Group>
        </Col>
      );
    }

    if (row.option === "sizeCode") {
      return (
        <Col className="px-3" xs={10} sm={12} md={7} lg={7}>
          <Form.Group className="mb-3" controlId={commonId}>
            <Form.Label className="col-form-label">Select Size Code</Form.Label>
            <DropdownListField
              required
              id="sizeCode"
              name="name"
              className="dropdownHive w-full rounded-md"
              data={allSizeCode}
              onChange={(value) => updateValue(row.id, value?.id)}
              placeholder={"Please Select sizeCode"}
              autoFocus={true}
            />
          </Form.Group>
        </Col>
      );
    }

    if (row.option === "deptCode") {
      return (
        <Col className="px-3" xs={10} sm={12} md={7} lg={7}>
          <Form.Group className="mb-3" controlId={commonId}>
            <Form.Label className="col-form-label">
              {t("msg_master_list.msg_department.department_name")}
            </Form.Label>
            <DropdownListField
              required
              id="deptCode"
              name="name"
              className="dropdownHive w-full rounded-md"
              data={allDepartmentMaster}
              onChange={(value) => updateValue(row.id, value?.id)}
              placeholder={"Please Select department"}
              autoFocus={true}
            />
          </Form.Group>
        </Col>
      );
    }

    if (row.option === "machineCode") {
      return (
        <Col className="px-3" xs={10} sm={12} md={7} lg={7}>
          <Form.Group className="mb-3" controlId={commonId}>
            <Form.Label className="col-form-label">
              {t("msg_master_list.msg_machines.name")}
            </Form.Label>
            <DropdownListField
              required
              id="machineCode"
              name="name"
              className="dropdownHive w-full rounded-md"
              data={allMachineMaster}
              onChange={(value) => updateValue(row.id, value?.id)}
              placeholder={"Please Select Machine"}
              autoFocus={true}
            />
          </Form.Group>
        </Col>
      );
    }

    if (row.option === "QC_Status") {
      return (
        <Col className="px-3" xs={10} sm={12} md={7} lg={7}>
          <Form.Group className="mb-3" controlId={commonId}>
            <Form.Label className="col-form-label">Select Status</Form.Label>
            <DropdownListField
              required
              id="QC_Status"
              name="name"
              className="dropdownHive w-full rounded-md"
              data={qc_status_data}
              onChange={(value) => updateValue(row.id, value?.id)}
              placeholder={"Please Select Status"}
              autoFocus={true}
            />
          </Form.Group>
        </Col>
      );
    }

    if (row.option === "jobcardStatus") {
      return (
        <Col className="px-3" xs={10} sm={12} md={7} lg={7}>
          <Form.Group className="mb-3" controlId={commonId}>
            <Form.Label className="col-form-label">Select Status</Form.Label>
            <DropdownListField
              required
              id="jobcardStatusId"
              name="name"
              className="dropdownHive w-full rounded-md"
              data={jobcardStatusdata}
              onChange={(value) => updateValue(row.id, value?.id)}
              placeholder={t("msg_master_list.msg_machines.select_department")}
              autoFocus={true}
            />
          </Form.Group>
        </Col>
      );
    }

    // customer
    if (row.option === "customer") {
      return (
        <Col className="px-3" xs={10} sm={12} md={7} lg={7}>
          <Form.Group className="mb-3" controlId={commonId}>
            <Form.Label className="col-form-label">
              {t("msg_reports.msg_production_status.customer")}
            </Form.Label>
            <DropdownListField
              required
              id="customer"
              name="name"
              className="dropdownHive w-full rounded-md"
              data={allCustomerMaster}
              onChange={(value) => updateValue(row.id, value?.id)}
              placeholder={"Please Select Customer"}
              autoFocus={true}
            />
          </Form.Group>
        </Col>
      );
    }

    // plan
    if (row.option === "plan") {
      return (
        <Col className="px-3" xs={10} sm={12} md={4} lg={4}>
          <Form.Group className="mb-3" controlId={commonId}>
            <Form.Label className="col-form-label">
              {t("msg_reports.msg_production_status.plan")}
            </Form.Label>
            <DatePicker
              placeholder="Select Month"
              parse={["MMM yyyy"]}
              max={new Date()}
              // row.value is stored as string, convert to Date for the picker
              value={row.value ? new Date(row.value) : null}
              onChange={(date) => {
                const formatted =
                  date && moment(date).isValid()
                    ? moment(date).format("YYYY-MM-DD") // or "MMM YYYY" if you prefer
                    : "";
                updateValue(row.id, formatted);
              }}
              valueFormat={{ month: "short", year: "numeric" }}
              calendarProps={{ views: ["year", "decade", "century"] }}
            />
          </Form.Group>
        </Col>
      );
    }

    // planType
    if (row.option === "planType") {
      return (
        <Col className="px-3" xs={10} sm={12} md={7} lg={7}>
          <Form.Group className="mb-3" controlId={commonId}>
            <Form.Label className="col-form-label">
              {t("msg_reports.msg_production_status.planType")}
            </Form.Label>
            <DropdownListField
              required
              id="planType"
              name="name"
              className="dropdownHive w-full rounded-md"
              data={allPlanTypeMaster}
              onChange={(value) => updateValue(row.id, value?.id)}
              placeholder={"Please Select planType"}
              autoFocus={true}
            />
          </Form.Group>
        </Col>
      );
    }

    // consumptionType
    if (row.option === "consumptionType") {
      return (
        <Col className="px-3" xs={10} sm={12} md={7} lg={7}>
          <Form.Group className="mb-3" controlId={commonId}>
            <Form.Label className="col-form-label">Consumption Type</Form.Label>
            <DropdownListField
              required
              id="consumptionType"
              name="name"
              className="dropdownHive w-full rounded-md"
              data={allConsumptionTypes}
              onChange={(value) => updateValue(row.id, value?.id)}
              placeholder={"Please Select Consumption Type"}
              autoFocus={true}
            />
          </Form.Group>
        </Col>
      );
    }

    // consumptionCode
    if (row.option === "consumptionCode") {
      return (
        <Col className="px-3" xs={10} sm={12} md={7} lg={7}>
          <Form.Group className="mb-3" controlId={commonId}>
            <Form.Label className="col-form-label">
              Enter the Consumption Code:
            </Form.Label>
            <Form.Control
              type="text"
              value={row.value || ""}
              placeholder="e.g: CON001"
              onChange={(e) => updateValue(row.id, e.target.value)}
              maxLength={Constants.MASTER.VALIDATION.maxLengthName}
              required
            />
            <Form.Control.Feedback type="invalid">
              {t("msg_master_list.msg_department.department_name_validation")}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      );
    }

    // Custom (date range) → value / value2
    if (row.option === "Custom") {
      return (
        <>
          <Col className="px-3" xs={10} sm={5} md={2} lg={2}>
            <Form.Group className="mb-3" controlId={commonId}>
              <Form.Label className="col-form-label">From Date</Form.Label>
              <DatePicker
                required
                placeholder="From Date"
                name="requiredFrom"
                value={row.value ? new Date(row.value) : null}
                valueFormat={{ dateStyle: "medium" }}
                onChange={(date) => updateDates(row.id, "value", date)}
                autoFocus
              />
            </Form.Group>
          </Col>
          <Col className="px-3" xs={10} sm={5} md={2} lg={2}>
            <Form.Group className="mb-3" controlId={commonId}>
              <Form.Label className="col-form-label">To Date</Form.Label>
              <DatePicker
                required
                placeholder="To Date"
                name="requiredTo"
                value={row.value2 ? new Date(row.value2) : null}
                valueFormat={{ dateStyle: "medium" }}
                onChange={(date) => updateDates(row.id, "value2", date)}
              />
            </Form.Group>
          </Col>
        </>
      );
    }

    // Required Date (date range) → value / value2
    if (row.option === "required_date") {
      return (
        <>
          <Col className="px-3" xs={10} sm={5} md={2} lg={2}>
            <Form.Group className="mb-3" controlId={commonId}>
              <Form.Label className="col-form-label">From Date</Form.Label>
              <DatePicker
                required
                placeholder="From Date"
                name="required_date_from"
                value={row.value ? new Date(row.value) : null}
                valueFormat={{ dateStyle: "medium" }}
                onChange={(date) => updateDates(row.id, "value", date)}
              />
            </Form.Group>
          </Col>
          <Col className="px-3" xs={10} sm={5} md={2} lg={2}>
            <Form.Group className="mb-3" controlId={commonId}>
              <Form.Label className="col-form-label">To Date</Form.Label>
              <DatePicker
                required
                placeholder="To Date"
                name="required_date_to"
                value={row.value2 ? new Date(row.value2) : null}
                valueFormat={{ dateStyle: "medium" }}
                onChange={(date) => updateDates(row.id, "value2", date)}
              />
            </Form.Group>
          </Col>
        </>
      );
    }

    return null;
  };

  useEffect(() => {
    setAllSizeCode(getAllSizeCode || []);
    setAllDepartmentMaster(getAllDepartmentMaster || []);
    setAllMachineMaster(getAllMachineMaster || []);
    setAllCustomerMaster(getAllCustomerMaster || []);
    setConsumptionTypes(getAllConsumptionTypes || []);

    const updated = getAllPlanTypeMaster.map((p) => ({
      ...p,
      id: p.name, // make id same as name
    }));

    setAllPlanTypeMaster(updated || []);
  }, [
    getAllSizeCode,
    getAllDepartmentMaster,
    getAllMachineMaster,
    getAllCustomerMaster,
    getAllPlanTypeMaster,
    getAllConsumptionTypes,
  ]);

  return (
    <Form noValidate ref={formRef}>
      <Row className="px-3">
        {filters.map((row, idx) => (
          <React.Fragment key={row.id}>
            <Col className="px-3" sm={12} md={4} lg={4}>
              <Form.Group className="mb-3" controlId={`select-${row.id}`}>
                <Form.Label className="col-form-label">
                  {t("msg_common.select_filter_option")}{" "}
                  {filters.length > 1 ? `#${idx + 1}` : ""} :
                </Form.Label>
                <Form.Select
                  aria-label={`select filter ${idx + 1}`}
                  size="md"
                  value={row.option}
                  onChange={(e) => updateOption(row.id, e.target.value)}
                >
                  {allowedOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {renderRowInput(row)}

            {/* Remove button per row (disabled if it's the only row) */}
            <Col
              className=""
              xs={1}
              md={1}
              lg={1}
              style={{ marginTop: 38, paddingLeft: 20 }}
            >
              <Button
                className="btn btn-danger"
                type="button"
                disabled={filters.length === 1}
                onClick={() => removeFilter(row.id)}
                title="Remove this filter"
              >
                <i className="bi bi-trash3"></i>
              </Button>
            </Col>
          </React.Fragment>
        ))}

        {/* Add Filter button */}
        <Col className="px-3 d-flex align-items-end" sm={12} md={4} lg={4}>
          <Button variant="outline-primary" type="button" onClick={addFilter}>
            + {t("msg_common.add") || "Add Filter"}
          </Button>
        </Col>

        {/* Action buttons */}
        <Col className="filterbox-btn" sm={8} md={6} lg={6}>
          <Button
            type="button"
            size="md"
            variant="primary"
            className="btn btn-primary btn-xs"
            onClick={handleSearch}
          >
            {t("msg_common.search")}
          </Button>

          <Button
            type="button"
            size="md"
            variant="danger"
            className="btn btn-primary btn-xs"
            onClick={handleCancel}
          >
            {t("msg_common.cancel")}
          </Button>

          {props.exportBtn !== false && (
            <Button
              type="button"
              size="md"
              variant="secondary"
              className="btn btn-primary btn-xs"
              disabled={props.downloadProgress}
              onClick={() => handleDownload()}
            >
              {props.downloadProgress ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Exporting...
                </>
              ) : (
                <>
                  <i className="bi bi-download" /> {"Export"}
                </>
              )}

            </Button>
          )}

          {props.pageName === "CHEMICAL_BATCH_REPORT" && (
            <Button
              type="button"
              size="md"
              variant="secondary"
              className="btn btn-primary btn-xs"
              onClick={() => handleDownload(true)}
            >
              <i className="bi bi-download" /> {"Export Consumables Batches"}
            </Button>
          )}
        </Col>
      </Row>
    </Form>
  );
};

export default ProdStatusFilterBox;
